import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Plus, Trash2, Star, Pencil, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { registerFontFace } from "@/lib/fonts";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SAMPLE = "The quick brown fox jumps over the lazy dog";

function AddFontModal({ open, onClose, onSaved, editing }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", family: "", category: "General", tags: "", status: "enabled", display_order: 0, featured: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      if (editing) setForm({ name: editing.name || "", family: editing.family || "", category: editing.category || "General", tags: (editing.tags || []).join(", "), status: editing.status || "enabled", display_order: editing.display_order || 0, featured: !!editing.featured });
      else setForm({ name: "", family: "", category: "General", tags: "", status: "enabled", display_order: 0, featured: false });
      setFile(null);
    }
  }, [open, editing]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const saved = await api.adminFontUpdate(editing.id, {
          name: form.name, family: form.family, category: form.category,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
          status: form.status, display_order: Number(form.display_order) || 0, featured: !!form.featured,
        });
        toast.success("Font updated");
        onSaved(saved);
      } else {
        if (!file) { toast.error("Please choose a font file"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", form.name); fd.append("family", form.family);
        fd.append("category", form.category); fd.append("tags", form.tags);
        fd.append("status", form.status); fd.append("display_order", String(Number(form.display_order) || 0));
        fd.append("featured", form.featured ? "true" : "false");
        const saved = await api.adminFontCreate(fd);
        if (saved.family && saved.storage_path) await registerFontFace(saved.family, saved.storage_path);
        toast.success(`${saved.name} added`);
        onSaved(saved);
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save font");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/10 bg-[#111] text-white">
        <DialogTitle>{editing ? "Edit Font" : "Add Font"}</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">{editing ? "Update this font's details." : "Upload a TTF, OTF, WOFF or WOFF2 file."}</DialogDescription>
        <div className="mt-2 space-y-3">
          {!editing && (
            <div>
              <input ref={fileRef} type="file" accept=".ttf,.otf,.woff,.woff2" className="hidden" data-testid="fm-file-input" onChange={(e) => { const f = e.target.files?.[0]; setFile(f); if (f && !form.name) set("name", f.name.replace(/\.[^.]+$/, "")); }} />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="fm-choose-file"
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-white/[0.02] py-3 text-sm font-semibold hover:border-[#00F0FF]/50">
                <Upload size={15} /> {file ? file.name : "Choose font file (TTF / OTF / WOFF / WOFF2)"}
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Font name</span>
              <input data-testid="fm-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Bebas Neue" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
            <label className="block"><span className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Font family {editing ? "" : "(auto-detected)"}</span>
              <input data-testid="fm-family" value={form.family} onChange={(e) => set("family", e.target.value)} placeholder="Bebas Neue" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Category</span>
              <input data-testid="fm-category" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Display" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
            <label className="block"><span className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Display order</span>
              <input data-testid="fm-order" type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
          </div>
          <label className="block"><span className="mb-1 block text-[11px] uppercase text-[#A1A1AA]">Tags (comma separated)</span>
            <input data-testid="fm-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Bold, Sports, Headlines" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="fm-status" checked={form.status === "enabled"} onChange={(e) => set("status", e.target.checked ? "enabled" : "disabled")} /> Enabled</label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="fm-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="fm-save" className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />} Save Font
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FontManagement() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(() => {
    api.adminFontsList({ q: q || undefined, category: category || undefined, status: status || undefined })
      .then((d) => { setData(d); (d.fonts || []).forEach((f) => f.family && f.storage_path && registerFontFace(f.family, f.storage_path)); })
      .catch(() => setData({ fonts: [], categories: [] }));
  }, [q, category, status]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const toggleStatus = async (f) => {
    try { const s = await api.adminFontUpdate(f.id, { status: f.status === "enabled" ? "disabled" : "enabled" }); setData((d) => ({ ...d, fonts: d.fonts.map((x) => x.id === f.id ? s : x) })); }
    catch { toast.error("Update failed"); }
  };
  const toggleFeatured = async (f) => {
    try { const s = await api.adminFontUpdate(f.id, { featured: !f.featured }); setData((d) => ({ ...d, fonts: d.fonts.map((x) => x.id === f.id ? s : x) })); }
    catch { toast.error("Update failed"); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This removes it from the user font selector.`)) return;
    try { await api.adminFontDelete(f.id); setData((d) => ({ ...d, fonts: d.fonts.filter((x) => x.id !== f.id) })); toast.success("Font deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="font-management">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg font-bold text-white">Font Management</div>
          <div className="text-xs text-[#A1A1AA]">Curate the fonts available to users. Enabled fonts appear in the shared font selector with a live preview.</div>
        </div>
        <button onClick={() => setModal({ open: true, editing: null })} data-testid="fm-add-btn" className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black"><Plus size={15} /> Add Font</button>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input data-testid="fm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fonts..." className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
        </div>
        <select data-testid="fm-cat-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
          <option value="">All categories</option>
          {(data?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="fm-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
          <option value="">All status</option><option value="enabled">Enabled</option><option value="disabled">Disabled</option>
        </select>
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div> :
        data.fonts.length === 0 ? <div className="rounded-2xl border border-white/10 py-16 text-center text-[#A1A1AA]" data-testid="fm-empty">No fonts yet. Click "Add Font" to upload one.</div> : (
          <div className="space-y-2">
            {data.fonts.map((f) => (
              <div key={f.id} data-testid={`fm-row-${f.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="min-w-[200px] flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-white" style={{ fontFamily: `"${f.family}", sans-serif` }}>{f.name}</span>
                    {f.featured && <Star size={12} className="fill-[#FFD400] text-[#FFD400]" />}
                    <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase text-[#A1A1AA]">{f.format}</span>
                  </div>
                  <div className="mt-0.5 truncate text-sm text-white/60" style={{ fontFamily: `"${f.family}", sans-serif` }}>{SAMPLE}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-[#A1A1AA]">
                    <span>{f.category}</span>
                    {(f.tags || []).map((t) => <span key={t} className="rounded bg-white/5 px-1.5 py-0.5">{t}</span>)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggleFeatured(f)} data-testid={`fm-feature-${f.id}`} title="Featured" className={`rounded-lg border border-white/15 p-2 ${f.featured ? "text-[#FFD400]" : "text-[#A1A1AA]"} hover:bg-white/5`}><Star size={14} className={f.featured ? "fill-[#FFD400]" : ""} /></button>
                  <button onClick={() => toggleStatus(f)} data-testid={`fm-toggle-${f.id}`} className={`rounded-lg border px-3 py-2 text-xs font-semibold ${f.status === "enabled" ? "border-[#22c55e]/40 text-[#22c55e]" : "border-white/15 text-[#A1A1AA]"} hover:bg-white/5`}>{f.status === "enabled" ? "Enabled" : "Disabled"}</button>
                  <button onClick={() => setModal({ open: true, editing: f })} data-testid={`fm-edit-${f.id}`} className="rounded-lg border border-white/15 p-2 text-[#A1A1AA] hover:bg-white/5 hover:text-white"><Pencil size={14} /></button>
                  <button onClick={() => del(f)} data-testid={`fm-delete-${f.id}`} className="rounded-lg border border-white/15 p-2 text-[#ef4444] hover:bg-white/5"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <AddFontModal open={modal.open} editing={modal.editing} onClose={() => setModal({ open: false, editing: null })}
        onSaved={() => load()} />
    </div>
  );
}
